import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import BulkEmailModal from "./BulkEmailModal";
import HospitalSupplierOrdersPanel from "./HospitalSupplierOrdersPanel";
import HospitalPipeline from "./HospitalPipeline";
import AddSalesRepDialog from "./AddSalesRepDialog";
import BulkAddContactsDialog from "./BulkAddContactsDialog";
import EditContactDialog from "./EditContactDialog";
import AddSingleContactDialog from "./AddSingleContactDialog";
import HospitalOrderRecipients from "./HospitalOrderRecipients";
import NetworkDrilldownDrawer from "./NetworkDrilldownDrawer";
import Net30Panel from "./Net30Panel";
import {
  Plus,
  Loader2,
  Building2,
  Users,
  Mail,
  Phone,
  MapPin,
  Link,
  Copy,
  ExternalLink,
  CheckCircle,
  Clock,
  UserPlus,
  ChevronRight,
  Search,
  X,
  Edit,
  Trash2,
  RefreshCw,
  Download,
  ArrowLeft,
  History,
  Shield,
  AlertTriangle,
  AlertCircle,
  MapPinned,
  Hospital,
  Send,
  List,
  LayoutGrid
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DEPARTMENTS = [
  "Supply Chain",
  "Materials Management",
  "Facilities Management",
  "Biomedical Engineering",
  "Environmental Services",
  "Nursing Administration",
  "Operating Room",
  "Emergency Department",
  "Radiology",
  "Laboratory",
  "Pharmacy",
  "IT/Technology",
  "Administration",
  "Other"
];

export default function HospitalManagement() {
  const { token, user } = useAuth();
  const [healthSystems, setHealthSystems] = useState([]);
  const [hospitalUsers, setHospitalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // View states
  const [selectedHealthSystem, setSelectedHealthSystem] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddRep, setShowAddRep] = useState(false);
  const [showBulkAddContacts, setShowBulkAddContacts] = useState(false);
  const [showAddSingleContact, setShowAddSingleContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [networkDrilldown, setNetworkDrilldown] = useState(null); // HS object when drilldown open
  const [viewMode, setViewMode] = useState("list"); // list, health-system-detail, user-detail
  const [listViewMode, setListViewMode] = useState("table"); // table or pipeline
  const [auditLogs, setAuditLogs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facilityFilter, setFacilityFilter] = useState("all"); // "all" or facility_id
  
  // Modal states
  const [showAddHealthSystem, setShowAddHealthSystem] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [selectedHospitalForEmail, setSelectedHospitalForEmail] = useState(null);
  const [showSetupLink, setShowSetupLink] = useState(false);
  const [showEditHealthSystem, setShowEditHealthSystem] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: 'health_system'|'user'|'facility', id, name}
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [showEditFacility, setShowEditFacility] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  
  // Form states
  const [healthSystemForm, setHealthSystemForm] = useState({
    name: "",
    short_name: "",
    allowed_email_domains: "",
    primary_contact_name: "",
    primary_contact_email: "",
    primary_contact_phone: "",
    billing_address: "",
    billing_city: "",
    billing_state: "",
    billing_zip: "",
    notes: "",
    contacts: []
  });
  const [bulkEmails, setBulkEmails] = useState({ department: "Supply Chain", text: "" });
  
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    title: "",
    department: "",
    phone: "",
    office_location: "",
    facility_id: "",
    is_active: true
  });
  
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    health_system_id: "",
    facility_id: ""
  });
  
  const [sendingEmail, setSendingEmail] = useState(null); // track which user's email is being sent
  
  const [facilityForm, setFacilityForm] = useState({
    name: "",
    facility_type: "hospital",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    notes: ""
  });
  
  const [setupLinkData, setSetupLinkData] = useState(null);
  const [reps, setReps] = useState([]);  // sales reps for inline assignment
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Deep-link support: /admin/hospitals?hs=<id> auto-opens that hospital's detail view.
  // Used by the "Manage hospital" → "View full page" button on the CRM dashboard.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const hsId = params.get("hs");
    if (hsId) {
      fetchHealthSystemDetail(hsId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [hsRes, usersRes] = await Promise.all([
        fetch(`${API}/health-systems`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/hospital-users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (hsRes.ok) {
        const data = await hsRes.json();
        setHealthSystems(data.health_systems || []);
      }
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setHospitalUsers(data.users || []);
      }

      // Pull sales reps for inline assignment dropdowns
      try {
        const repsRes = await fetch(`${API}/admin/sales-reps`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (repsRes.ok) {
          const data = await repsRes.json();
          setReps(data.reps || []);
        }
      } catch (e) { /* non-fatal */ }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const assignRepToHospital = async (hsId, repId, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      const res = await fetch(`${API}/health-systems/${hsId}/assign-rep`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ rep_id: repId || "__unassign__" }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      toast.success(data.created_crm_account ? "Assigned (also created CRM record)" : "Assignment updated");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ---- Network hierarchy ---------------------------------------------------
  // Mark a hospital as a parent network OR assign one as a child of a parent.
  // Both calls hit the same PATCH endpoint; the backend handles the diff.
  const setParentNetwork = async (hsId, parentId, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      const res = await fetch(`${API}/health-systems/${hsId}/parent`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ parent_id: parentId || null }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success(parentId ? "Attached to network" : "Detached from network");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const toggleNetworkRole = async (hsId, makeNetwork, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      const res = await fetch(`${API}/health-systems/${hsId}/parent`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ is_network: makeNetwork }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success(makeNetwork ? "Marked as a network parent" : "Demoted from network role");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchHealthSystemDetail = async (hsId) => {
    try {
      const res = await fetch(`${API}/admin/health-systems/${hsId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedHealthSystem(data.health_system);
        setHospitalUsers(data.users || []);
        setAuditLogs(data.audit_logs || []);
        setFacilities(data.health_system?.facilities || []);
        setViewMode("health-system-detail");
      }
    } catch (err) {
      toast.error("Failed to load health system details");
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/hospital-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedUser(data.user);
        setAuditLogs(data.audit_logs || []);
        setViewMode("user-detail");
      }
    } catch (err) {
      toast.error("Failed to load user details");
    }
  };

  const handleAddHealthSystem = async () => {
    if (!healthSystemForm.name) {
      toast.error("Health system name is required");
      return;
    }

    // Validate contacts that are partially filled
    const contacts = (healthSystemForm.contacts || []).filter(c => c.name || c.email);
    for (const c of contacts) {
      if (!c.email) {
        toast.error("Each contact needs an email address (name is optional)");
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        ...healthSystemForm,
        allowed_email_domains: healthSystemForm.allowed_email_domains
          ? healthSystemForm.allowed_email_domains.split(",").map(d => d.trim()).filter(Boolean)
          : [],
        contacts: contacts.map(c => ({
          name: c.name || c.email.split("@")[0],
          email: c.email,
          phone: c.phone || null,
          title: c.title || null,
          department: c.department || null,
          is_primary: !!c.is_primary,
          notes: c.notes || null,
        })),
      };
      // Strip blank primary_contact_email to avoid Pydantic EmailStr rejection
      if (!payload.primary_contact_email) {
        delete payload.primary_contact_email;
      }

      const res = await fetch(`${API}/health-systems`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const created = await res.json().catch(() => null);
        toast.success("Health system added — scroll down to activate Net 30 if needed.");
        setShowAddHealthSystem(false);
        resetHealthSystemForm();
        await fetchData();
        // Drill into the newly created hospital so the admin can immediately
        // click "Activate as Net 30 Customer" on its profile.
        if (created?.id || created?.health_system?.id) {
          const newId = created?.id || created?.health_system?.id;
          fetchHealthSystemDetail(newId);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        const detail = typeof err.detail === "string"
          ? err.detail
          : (Array.isArray(err.detail) ? err.detail.map(e => `${e.loc?.slice(-1)[0] || "field"}: ${e.msg}`).join("; ") : "Failed to add health system");
        toast.error(detail);
      }
    } catch (err) {
      toast.error("Failed to add health system");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateHealthSystem = async () => {
    if (!selectedHealthSystem) return;

    const contacts = (healthSystemForm.contacts || []).filter(c => c.name || c.email);
    for (const c of contacts) {
      if (!c.email) {
        toast.error("Each contact needs an email address (name is optional)");
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        ...healthSystemForm,
        allowed_email_domains: healthSystemForm.allowed_email_domains
          ? (typeof healthSystemForm.allowed_email_domains === 'string'
              ? healthSystemForm.allowed_email_domains.split(",").map(d => d.trim()).filter(Boolean)
              : healthSystemForm.allowed_email_domains)
          : [],
        contacts: contacts.map(c => ({
          id: c.id || undefined,
          name: c.name || c.email.split("@")[0],
          email: c.email,
          phone: c.phone || null,
          title: c.title || null,
          department: c.department || null,
          is_primary: !!c.is_primary,
          notes: c.notes || null,
        })),
      };

      const res = await fetch(`${API}/admin/health-systems/${selectedHealthSystem.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success("Health system updated successfully");
        setShowEditHealthSystem(false);
        fetchHealthSystemDetail(selectedHealthSystem.id);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        const detail = typeof err.detail === "string"
          ? err.detail
          : (Array.isArray(err.detail) ? err.detail.map(e => `${e.loc?.slice(-1)[0] || "field"}: ${e.msg}`).join("; ") : "Failed to update health system");
        toast.error(detail);
      }
    } catch (err) {
      toast.error("Failed to update health system");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/hospital-users/${selectedUser.id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(userForm)
      });
      
      if (res.ok) {
        toast.success("User updated successfully");
        setShowEditUser(false);
        fetchUserDetail(selectedUser.id);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update user");
      }
    } catch (err) {
      toast.error("Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setSubmitting(true);
    try {
      let endpoint;
      let method = "DELETE";
      let body = null;
      
      if (deleteTarget.type === 'health_system') {
        endpoint = `${API}/admin/health-systems/${deleteTarget.id}`;
      } else if (deleteTarget.type === 'facility') {
        endpoint = `${API}/admin/health-systems/${selectedHealthSystem.id}/facilities/${deleteTarget.id}`;
      } else {
        // Use force-delete for users to ensure complete cleanup
        endpoint = `${API}/admin/users/force-delete`;
        method = "POST";
        body = JSON.stringify({
          userIds: [deleteTarget.id],
          hospitalId: selectedHealthSystem?.id || deleteTarget.health_system_id
        });
      }
      
      const res = await fetch(endpoint, {
        method: method,
        headers: { 
          Authorization: `Bearer ${token}`,
          ...(body && { "Content-Type": "application/json" })
        },
        ...(body && { body: body })
      });
      
      if (res.ok) {
        const result = await res.json();
        const typeLabel = deleteTarget.type === 'health_system' ? 'Health system' : 
                          deleteTarget.type === 'facility' ? 'Facility' : 'User';
        toast.success(`${typeLabel} deleted successfully`);
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
        
        if (deleteTarget.type === 'health_system') {
          setViewMode("list");
          setSelectedHealthSystem(null);
        } else if (deleteTarget.type === 'facility') {
          fetchHealthSystemDetail(selectedHealthSystem.id);
        } else if (viewMode === "user-detail") {
          setViewMode("health-system-detail");
          if (selectedHealthSystem) {
            fetchHealthSystemDetail(selectedHealthSystem.id);
          }
        }
        // Force refresh data from server
        await fetchData();
        // Also refresh health system detail if viewing one
        if (selectedHealthSystem) {
          await fetchHealthSystemDetail(selectedHealthSystem.id);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete");
    } finally {
      setSubmitting(false);
    }
  };

  // Facility Management Handlers
  const handleAddFacility = async () => {
    if (!facilityForm.name) {
      toast.error("Facility name is required");
      return;
    }
    if (!selectedHealthSystem) {
      toast.error("No health system selected. Please select a health system first.");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/health-systems/${selectedHealthSystem.id}/facilities`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          name: facilityForm.name,
          facility_type: facilityForm.facility_type || "hospital",
          address: facilityForm.address,
          city: facilityForm.city,
          state: facilityForm.state,
          zip_code: facilityForm.zip_code,
          phone: facilityForm.phone
        })
      });
      
      if (res.ok) {
        toast.success("Facility added successfully");
        setShowAddFacility(false);
        resetFacilityForm();
        fetchHealthSystemDetail(selectedHealthSystem.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add facility");
      }
    } catch (err) {
      toast.error("Failed to add facility");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateFacility = async () => {
    if (!selectedFacility || !selectedHealthSystem) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/admin/health-systems/${selectedHealthSystem.id}/facilities/${selectedFacility.id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(facilityForm)
      });
      
      if (res.ok) {
        toast.success("Facility updated successfully");
        setShowEditFacility(false);
        setSelectedFacility(null);
        fetchHealthSystemDetail(selectedHealthSystem.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update facility");
      }
    } catch (err) {
      toast.error("Failed to update facility");
    } finally {
      setSubmitting(false);
    }
  };

  const resetFacilityForm = () => {
    setFacilityForm({
      name: "",
      facility_type: "hospital",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      phone: "",
      notes: ""
    });
  };

  const openEditFacility = (facility) => {
    setFacilityForm({
      name: facility.name || "",
      address: facility.address || "",
      city: facility.city || "",
      state: facility.state || "",
      zip_code: facility.zip_code || "",
      phone: facility.phone || "",
      notes: facility.notes || ""
    });
    setSelectedFacility(facility);
    setShowEditFacility(true);
  };

  const handleResendSetupLink = async (userId) => {
    try {
      // First generate a new setup link
      const res = await fetch(`${API}/admin/hospital-users/${userId}/resend-setup-link`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Now send the setup email
        const emailRes = await fetch(`${API}/admin/hospital-users/${userId}/send-setup-email`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (emailRes.ok) {
          const emailData = await emailRes.json();
          setSetupLinkData({ ...data, email_sent: emailData.email_sent });
          setShowSetupLink(true);
          toast.success("Setup link emailed successfully!");
        } else {
          // Email failed but link was generated
          setSetupLinkData(data);
          setShowSetupLink(true);
          toast.success("Setup link generated! (Email delivery pending)");
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to generate setup link");
      }
    } catch (err) {
      toast.error("Failed to send setup link");
    }
  };

  const handleInviteUser = async () => {
    if (!inviteForm.name || !inviteForm.email || !inviteForm.health_system_id) {
      toast.error("Please fill all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        name: inviteForm.name,
        email: inviteForm.email,
        health_system_id: inviteForm.health_system_id
      };
      // Only include facility_id if selected
      if (inviteForm.facility_id && inviteForm.facility_id !== "__add_new__") {
        payload.facility_id = inviteForm.facility_id;
      }
      
      const res = await fetch(`${API}/admin/hospital-users/invite`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        setSetupLinkData(data);
        setShowInviteUser(false);
        setShowSetupLink(true);
        setInviteForm({ name: "", email: "", health_system_id: selectedHealthSystem?.id || "", facility_id: "" });
        fetchData();
        if (selectedHealthSystem) {
          fetchHealthSystemDetail(selectedHealthSystem.id);
        }
        // Show appropriate message based on email status
        if (data.email_sent) {
          toast.success(`Setup email sent to ${inviteForm.email}!`);
        } else {
          toast.success("User invited! Setup link generated (email may be pending).");
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to invite user");
      }
    } catch (err) {
      toast.error("Failed to invite user");
    } finally {
      setSubmitting(false);
    }
  };

  const resetHealthSystemForm = () => {
    setHealthSystemForm({
      name: "", short_name: "", allowed_email_domains: "",
      primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
      billing_address: "", billing_city: "", billing_state: "", billing_zip: "", notes: "",
      contacts: []
    });
  };

  const openEditHealthSystem = (hs) => {
    setHealthSystemForm({
      name: hs.name || "",
      short_name: hs.short_name || "",
      allowed_email_domains: Array.isArray(hs.allowed_email_domains) 
        ? hs.allowed_email_domains.join(", ") 
        : hs.allowed_email_domains || "",
      primary_contact_name: hs.primary_contact_name || "",
      primary_contact_email: hs.primary_contact_email || "",
      primary_contact_phone: hs.primary_contact_phone || "",
      billing_address: hs.billing_address || "",
      billing_city: hs.billing_city || "",
      billing_state: hs.billing_state || "",
      billing_zip: hs.billing_zip || "",
      notes: hs.notes || "",
      contacts: Array.isArray(hs.contacts) ? hs.contacts : []
    });
    setShowEditHealthSystem(true);
  };

  const openEditUser = (u) => {
    setUserForm({
      name: u.name || "",
      email: u.email || "",
      title: u.title || "",
      department: u.department || "",
      phone: u.phone || "",
      office_location: u.office_location || "",
      facility_id: u.facility_id || "",
      is_active: u.is_active !== false
    });
    setShowEditUser(true);
  };

  const copySetupLink = () => {
    if (setupLinkData?.setup_link) {
      navigator.clipboard.writeText(setupLinkData.setup_link);
      toast.success("Link copied to clipboard!");
    }
  };

  // Send setup email to pending user
  const handleSendSetupEmail = async (user) => {
    setSendingEmail(user.id);
    try {
      const res = await fetch(`${API}/admin/hospital-users/${user.id}/send-setup-email`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        }
      });
      
      if (res.ok) {
        toast.success(`Setup email sent to ${user.email}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to send email");
      }
    } catch (err) {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(null);
    }
  };

  const filteredHealthSystems = healthSystems.filter(hs =>
    hs.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hs.short_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = hospitalUsers.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.health_system_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSuperAdmin = user?.role === "super_admin";

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Health System Detail View
  if (viewMode === "health-system-detail" && selectedHealthSystem) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setViewMode("list"); setSelectedHealthSystem(null); }}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{selectedHealthSystem.name}</h2>
              <p className="text-slate-500 text-sm">{selectedHealthSystem.short_name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openEditHealthSystem(selectedHealthSystem)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            {isSuperAdmin && (
              <Button 
                variant="outline" 
                className={selectedHealthSystem.lifecycle_state === "lead" || selectedHealthSystem.pipeline_status === "pending"
                  ? "text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  : "text-amber-600 border-amber-200 hover:bg-amber-50"}
                onClick={async () => {
                  const isLead = selectedHealthSystem.lifecycle_state === "lead" || selectedHealthSystem.pipeline_status === "pending";
                  const newStatus = isLead ? "customer" : "lead";
                  const verb = isLead ? "promote back to customer" : "convert back to a lead";
                  const reason = !isLead ? (window.prompt(`Reason for converting "${selectedHealthSystem.name}" back to a lead?`, "No longer active") || null) : null;
                  if (!isLead && reason === null) return; // user cancelled
                  try {
                    const res = await fetch(`${API}/admin/health-systems/${selectedHealthSystem.id}/lifecycle`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ status: newStatus, reason }),
                    });
                    if (!res.ok) {
                      const e = await res.json().catch(() => ({}));
                      throw new Error(e.detail || `Failed to ${verb}`);
                    }
                    toast.success(`${selectedHealthSystem.name} ${newStatus === "lead" ? "moved back to lead" : "restored to customer"} (CRM synced)`);
                    fetchHealthSystemDetail(selectedHealthSystem.id);
                    fetchData();
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
                data-testid="lifecycle-toggle-btn"
              >
                {selectedHealthSystem.lifecycle_state === "lead" || selectedHealthSystem.pipeline_status === "pending" ? (
                  <><CheckCircle className="w-4 h-4 mr-2" />Promote to Customer</>
                ) : (
                  <><AlertTriangle className="w-4 h-4 mr-2" />Convert back to Lead</>
                )}
              </Button>
            )}
            {isSuperAdmin && (
              <Button 
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  setDeleteTarget({ type: 'health_system', id: selectedHealthSystem.id, name: selectedHealthSystem.name });
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Health System Info Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-slate-700 mb-3">Primary Contact</h4>
              <div className="space-y-2 text-sm">
                <p className="text-slate-900 font-medium">{selectedHealthSystem.primary_contact_name || '-'}</p>
                {selectedHealthSystem.primary_contact_email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {selectedHealthSystem.primary_contact_email}
                  </div>
                )}
                {selectedHealthSystem.primary_contact_phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {selectedHealthSystem.primary_contact_phone}
                  </div>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-slate-700 mb-3">Billing Address</h4>
              <div className="text-sm text-slate-600">
                <p>{selectedHealthSystem.billing_address || '-'}</p>
                {selectedHealthSystem.billing_city && (
                  <p>{selectedHealthSystem.billing_city}, {selectedHealthSystem.billing_state} {selectedHealthSystem.billing_zip}</p>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-slate-700 mb-3">Allowed Email Domains</h4>
              <div className="flex flex-wrap gap-1">
                {selectedHealthSystem.allowed_email_domains?.length > 0 ? (
                  selectedHealthSystem.allowed_email_domains.map(domain => (
                    <span key={domain} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      @{domain}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-slate-500">None configured</span>
                )}
              </div>
            </div>
          </div>
          {selectedHealthSystem.notes && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <h4 className="font-medium text-slate-700 mb-2">Notes</h4>
              <p className="text-sm text-slate-600">{selectedHealthSystem.notes}</p>
            </div>
          )}

          {/* Net 30 / Flash Funding factoring workflow */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <Net30Panel
              healthSystemId={selectedHealthSystem.id}
              healthSystemName={selectedHealthSystem.name}
              token={token}
            />
          </div>

          {/* Assigned Sales Rep */}
          <div className="mt-4 pt-4 border-t border-slate-200" data-testid="hs-detail-rep-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium text-slate-700">Assigned Sales Rep</h4>
                {selectedHealthSystem.assigned_rep_name ? (
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs gap-1">
                    <CheckCircle className="w-3 h-3" /> {selectedHealthSystem.assigned_rep_name}
                  </Badge>
                ) : (
                  <Badge className="bg-rose-100 text-rose-700 text-xs gap-1">
                    <Clock className="w-3 h-3" /> Unassigned
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedHealthSystem.assigned_rep_id || ""}
                  onChange={async (e) => {
                    await assignRepToHospital(selectedHealthSystem.id, e.target.value);
                    await fetchHealthSystemDetail(selectedHealthSystem.id);
                  }}
                  className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white min-w-[200px]"
                  data-testid="hs-detail-rep-select"
                >
                  <option value="">— Unassigned —</option>
                  {reps.filter(r => r.is_active !== false).map((r) => (
                    <option key={r.id} value={r.id}>{r.name} {r.role_label ? `· ${r.role_label}` : ""}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddRep(true)}
                  className="gap-1.5"
                  data-testid="hs-detail-add-rep-btn"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Add new rep
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Assigning a rep links this hospital to a CRM account (auto-created if none exists) and routes Email-Hospital CCs + commissions to the rep.
            </p>
          </div>

          {/* Permanent order-notification recipients */}
          <HospitalOrderRecipients
            healthSystemId={selectedHealthSystem.id}
            token={token}
            suggestions={(selectedHealthSystem.contacts || []).filter((c) => c.email)}
          />

          {/* Department Contacts (read-only display + inline bulk-add) */}
          <div className="mt-4 pt-4 border-t border-slate-200" data-testid="department-contacts-section">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                Department Contacts
                <Badge className="bg-blue-100 text-blue-800 text-xs">
                  {(selectedHealthSystem.contacts || []).length}
                </Badge>
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkAddContacts(true)}
                className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="hs-detail-bulk-add-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Bulk Add Contacts
              </Button>
            </div>

            <div className="flex justify-end -mt-1 mb-2">
              <Button
                size="sm"
                onClick={() => setShowAddSingleContact(true)}
                className="bg-blue-600 hover:bg-blue-700 gap-1.5 text-white"
                data-testid="hs-detail-add-single-contact-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Add contact
              </Button>
            </div>

            {(selectedHealthSystem.contacts || []).length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">
                  No department contacts yet. Click <strong>Add contact</strong> for a single entry (with title + department) or <strong>Bulk Add Contacts</strong> to paste many emails at once.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {selectedHealthSystem.contacts.map((c, i) => (
                  <div
                    key={c.id || i}
                    className="group relative rounded-lg border border-slate-200 bg-slate-50/50 p-3 hover:border-blue-300 transition-colors"
                    data-testid={`contact-display-${i}`}
                  >
                    <button
                      type="button"
                      onClick={() => setEditingContact(c)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-semibold px-2 py-1 rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
                      data-testid={`edit-contact-${i}`}
                    >
                      Edit
                    </button>
                    <div className="flex items-start justify-between gap-2 mb-1 pr-12">
                      <p className="font-semibold text-slate-900 text-sm leading-tight">{c.name}</p>
                      {c.is_primary && (
                        <Badge className="bg-emerald-100 text-emerald-800 text-[10px] shrink-0">Primary</Badge>
                      )}
                    </div>
                    {c.title && (
                      <p className="text-xs text-slate-500 mb-1.5">{c.title}</p>
                    )}
                    {c.department && (
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] mb-2 inline-block">{c.department}</Badge>
                    )}
                    <div className="space-y-1 text-xs text-slate-600 mt-1">
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-blue-600 transition-colors break-all">
                        <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="truncate">{c.email}</span>
                      </a>
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
                          <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                          {c.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Facilities Section */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="facilities-section">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Hospital className="w-5 h-5 text-emerald-600" />
              Facilities ({facilities.length})
            </h3>
            <Button 
              size="sm" 
              onClick={() => {
                resetFacilityForm();
                setShowAddFacility(true);
              }}
              data-testid="add-facility-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Facility
            </Button>
          </div>
          
          {facilities.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {facilities.map(facility => (
                <div key={facility.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`facility-${facility.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <MapPinned className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{facility.name}</h4>
                        {(facility.address || facility.city) && (
                          <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <MapPin className="w-3 h-3" />
                            <span>
                              {facility.address && `${facility.address}, `}
                              {facility.city && `${facility.city}, `}
                              {facility.state} {facility.zip}
                            </span>
                          </div>
                        )}
                        {facility.phone && (
                          <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <Phone className="w-3 h-3" />
                            <span>{facility.phone}</span>
                          </div>
                        )}
                        {facility.notes && (
                          <p className="text-xs text-slate-400 mt-2">{facility.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => openEditFacility(facility)} 
                        title="Edit facility"
                        data-testid={`edit-facility-${facility.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setDeleteTarget({ type: 'facility', id: facility.id, name: facility.name });
                          setShowDeleteConfirm(true);
                        }}
                        title="Delete facility"
                        data-testid={`delete-facility-${facility.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-12 text-center text-slate-500">
              <MapPinned className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No facilities yet</p>
              <p className="text-xs mt-1">Click &quot;Add Facility&quot; to add locations</p>
            </div>
          )}
        </div>

        {/* Users Section */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Users ({hospitalUsers.length})
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Facility Filter */}
              {facilities.length > 1 && (
                <Select value={facilityFilter} onValueChange={setFacilityFilter}>
                  <SelectTrigger className="w-[180px] h-9 text-sm">
                    <SelectValue placeholder="Filter by facility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Facilities</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {facilities.map(f => (
                      <SelectItem key={f.id} value={f.id}>
                        <span className="flex items-center gap-2">
                          <MapPinned className="w-3 h-3" />
                          {f.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button 
                size="sm"
                variant="outline"
                onClick={() => {
                  setSelectedHospitalForEmail(selectedHealthSystem);
                  setShowBulkEmailModal(true);
                }}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email All Users
              </Button>
              <Button 
                size="sm" 
                onClick={() => {
                  setInviteForm({ ...inviteForm, health_system_id: selectedHealthSystem.id });
                  setShowInviteUser(true);
                }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </div>
          </div>
          
          {/* Facility summary cards - show when multiple facilities */}
          {facilities.length > 1 && facilityFilter === "all" && (
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <div className="flex flex-wrap gap-2">
                {facilities.map(f => {
                  const userCount = hospitalUsers.filter(u => u.facility_id === f.id).length;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFacilityFilter(f.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all text-sm"
                    >
                      <MapPinned className="w-4 h-4 text-emerald-600" />
                      <span className="font-medium">{f.name}</span>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">{userCount}</span>
                    </button>
                  );
                })}
                {hospitalUsers.filter(u => !u.facility_id).length > 0 && (
                  <button
                    onClick={() => setFacilityFilter("unassigned")}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-amber-200 rounded-lg hover:border-amber-300 hover:bg-amber-50 transition-all text-sm"
                  >
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <span className="font-medium">Unassigned</span>
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full text-xs">
                      {hospitalUsers.filter(u => !u.facility_id).length}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
          
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">User</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Facility</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Password</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {hospitalUsers
                .filter(u => {
                  if (facilityFilter === "all") return true;
                  if (facilityFilter === "unassigned") return !u.facility_id;
                  return u.facility_id === facilityFilter;
                })
                .map(u => {
                const userFacility = facilities.find(f => f.id === u.facility_id);
                return (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <button 
                        onClick={() => fetchUserDetail(u.id)}
                        className="text-left hover:text-blue-600"
                      >
                        <p className="font-medium text-slate-900">{u.name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {userFacility ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-700 text-xs">
                          <MapPinned className="w-3 h-3" />
                          {userFacility.name}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.profile_complete ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : u.setup_token ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-400 italic">••••••••</span>
                    </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!u.profile_complete && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleResendSetupLink(u.id)} title="Email setup link" className="text-green-600 hover:bg-green-50">
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-blue-600 hover:bg-blue-50"
                            onClick={() => {
                              const setupLink = `${window.location.origin}/hospital/setup/${u.setup_token || u.id}`;
                              const subject = encodeURIComponent(`Welcome to Ask Isaac  - Your Account Setup`);
                              const body = encodeURIComponent(
`Hi ${u.name},

You've been invited to join Ask Isaac, powered by Sterile Mate — the healthcare facility management platform.

To complete your account setup, please click the link below:

SETUP LINK: ${setupLink}

You will create your own secure password during setup.

This link will expire in 7 days.

If you have any questions, please don't hesitate to reach out.

Best regards,
Ask Isaac Team

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
REVOLUTIONIZING HYGIENE IN HEALTHCARE

George Broughton III
President

396 W Greens Road, Suite 1139,
Suite 800A, 800B
Houston, TX 77067

Phone: 800.988.3892
Direct: 281.456.3458
Mobile: 800.988.3892
Email: george.broughton@sterilemate.com`
                              );
                              window.location.href = `mailto:${u.email}?subject=${subject}&body=${body}`;
                            }} 
                            title="Open in Outlook/Email"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedUser(u); openEditUser(u); }} title="Edit user">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setDeleteTarget({ type: 'user', id: u.id, name: u.name });
                          setShowDeleteConfirm(true);
                        }}
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
              })}
              {hospitalUsers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                    No users yet. Click &quot;Invite User&quot; to add staff.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Supplier Orders for this Hospital */}
        <HospitalSupplierOrdersPanel
          token={localStorage.getItem("sterilemate_token")}
          hospitalId={selectedHealthSystem?.id}
          hospitalName={selectedHealthSystem?.name}
        />

        {/* Audit Logs Section */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 p-4 border-b border-slate-200">
            <History className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Activity Log</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {auditLogs.length > 0 ? auditLogs.map(log => (
              <div key={log.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">
                    {log.action === 'create' && '✨ Created'}
                    {log.action === 'update' && '✏️ Updated'}
                    {log.action === 'delete' && '🗑️ Deleted'}
                    {log.action === 'invite' && '📨 Invited'}
                    {log.action === 'resend_setup_link' && '🔗 Resent setup link'}
                    {log.action === 'send_setup_email' && '📧 Sent setup email'}
                    {' '}{log.entity_type?.replace('_', ' ')}
                  </span>
                  <span className="text-slate-500 text-xs">
                    {new Date(log.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-slate-600 text-xs mt-1">
                  by {log.performed_by_email}
                </p>
              </div>
            )) : (
              <div className="px-4 py-8 text-center text-slate-500">
                No activity logs yet
              </div>
            )}
          </div>
        </div>

        {/* Modals - same as list view */}
        {renderModals()}
      </div>
    );
  }

  // User Detail View
  if (viewMode === "user-detail" && selectedUser) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => {
              setViewMode(selectedHealthSystem ? "health-system-detail" : "list");
              setSelectedUser(null);
            }}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{selectedUser.name}</h2>
              <p className="text-slate-500 text-sm">{selectedUser.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {!selectedUser.profile_complete && (
              <Button variant="outline" onClick={() => handleResendSetupLink(selectedUser.id)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Resend Setup Link
              </Button>
            )}
            <Button variant="outline" onClick={() => openEditUser(selectedUser)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => {
                setDeleteTarget({ type: 'user', id: selectedUser.id, name: selectedUser.name });
                setShowDeleteConfirm(true);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* User Info */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <Label className="text-slate-500">Health System</Label>
              <p className="font-medium text-slate-900">{selectedUser.health_system_name || '-'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Title</Label>
              <p className="font-medium text-slate-900">{selectedUser.title || '-'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Department</Label>
              <p className="font-medium text-slate-900">{selectedUser.department || '-'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Phone</Label>
              <p className="font-medium text-slate-900">{selectedUser.phone || '-'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Office Location</Label>
              <p className="font-medium text-slate-900">{selectedUser.office_location || '-'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Password</Label>
              <p className="text-slate-400 italic">••••••••  <span className="text-xs">(encrypted)</span></p>
            </div>
            <div>
              <Label className="text-slate-500">Status</Label>
              <div className="mt-1">
                {selectedUser.profile_complete ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3 h-3" />
                    Profile Complete
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    <Clock className="w-3 h-3" />
                    Pending Setup
                  </span>
                )}
              </div>
            </div>
            <div>
              <Label className="text-slate-500">Active</Label>
              <p className="font-medium text-slate-900">{selectedUser.is_active ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Created</Label>
              <p className="font-medium text-slate-900">
                {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Audit Logs */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 p-4 border-b border-slate-200">
            <History className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">User Activity Log</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {auditLogs.length > 0 ? auditLogs.map(log => (
              <div key={log.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">
                    {log.action === 'create' && '✨ Created'}
                    {log.action === 'update' && '✏️ Updated'}
                    {log.action === 'delete' && '🗑️ Deleted'}
                    {log.action === 'resend_setup_link' && '🔗 Setup link resent'}
                  </span>
                  <span className="text-slate-500 text-xs">
                    {new Date(log.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-slate-600 text-xs mt-1">by {log.performed_by_email}</p>
              </div>
            )) : (
              <div className="px-4 py-8 text-center text-slate-500">No activity logs yet</div>
            )}
          </div>
        </div>

        {renderModals()}
      </div>
    );
  }

  // Render modals function
  function renderModals() {
    return (
      <>
        {/* Add New Sales Rep — also accessible from hospital detail view */}
        <AddSalesRepDialog
          open={showAddRep}
          onOpenChange={setShowAddRep}
          token={token}
          currentUserRole={user?.role}
          onCreated={async (rep) => {
            await fetchData();
            if (selectedHealthSystem && rep?.id) {
              await assignRepToHospital(selectedHealthSystem.id, rep.id);
              await fetchHealthSystemDetail(selectedHealthSystem.id);
            }
          }}
        />

        {/* Bulk add contacts dialog — wired from the hospital detail view */}
        <BulkAddContactsDialog
          open={showBulkAddContacts}
          onOpenChange={setShowBulkAddContacts}
          hospitalId={selectedHealthSystem?.id}
          hospitalName={selectedHealthSystem?.name || "this hospital"}
          token={token}
          onSaved={() => {
            if (selectedHealthSystem) {
              fetchHealthSystemDetail(selectedHealthSystem.id);
            }
          }}
        />

        {/* Add a single department contact with title + department category */}
        <AddSingleContactDialog
          open={showAddSingleContact}
          onOpenChange={setShowAddSingleContact}
          hospital={selectedHealthSystem}
          token={token}
          onSaved={() => {
            if (selectedHealthSystem) {
              fetchHealthSystemDetail(selectedHealthSystem.id);
            }
          }}
        />

        {/* Edit a single department contact (full profile + invite-to-portal) */}
        <EditContactDialog
          open={!!editingContact}
          onOpenChange={(o) => { if (!o) setEditingContact(null); }}
          hospital={selectedHealthSystem}
          contact={editingContact}
          token={token}
          onSaved={() => {
            if (selectedHealthSystem) {
              fetchHealthSystemDetail(selectedHealthSystem.id);
            }
          }}
        />

        {/* Add Health System Modal */}
        <Dialog open={showAddHealthSystem} onOpenChange={setShowAddHealthSystem}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Add Health System
              </DialogTitle>
            </DialogHeader>
            {renderHealthSystemForm()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddHealthSystem(false)}>Cancel</Button>
              <Button onClick={handleAddHealthSystem} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Add Health System
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Health System Modal */}
        <Dialog open={showEditHealthSystem} onOpenChange={setShowEditHealthSystem}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600" />
                Edit Health System
              </DialogTitle>
            </DialogHeader>
            {renderHealthSystemForm()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditHealthSystem(false)}>Cancel</Button>
              <Button onClick={handleUpdateHealthSystem} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite User Modal */}
        <Dialog open={showInviteUser} onOpenChange={setShowInviteUser}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                Invite Hospital User
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Health System *</Label>
                <Select 
                  value={inviteForm.health_system_id} 
                  onValueChange={(value) => setInviteForm({...inviteForm, health_system_id: value, facility_id: ""})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select health system" />
                  </SelectTrigger>
                  <SelectContent>
                    {healthSystems.map(hs => (
                      <SelectItem key={hs.id} value={hs.id}>{hs.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {inviteForm.health_system_id && (
                <div>
                  <Label>Facility / Location</Label>
                  <Select 
                    value={inviteForm.facility_id || "__none__"} 
                    onValueChange={(value) => {
                      if (value === "__add_new__") {
                        setShowInviteUser(false);
                        resetFacilityForm();
                        setShowAddFacility(true);
                      } else if (value === "__none__") {
                        setInviteForm({...inviteForm, facility_id: ""});
                      } else {
                        setInviteForm({...inviteForm, facility_id: value});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No specific facility</SelectItem>
                      {/* Use selectedHealthSystem.facilities if in detail view, otherwise find from healthSystems */}
                      {(selectedHealthSystem?.id === inviteForm.health_system_id 
                        ? (selectedHealthSystem?.facilities || facilities || [])
                        : (healthSystems.find(hs => hs.id === inviteForm.health_system_id)?.facilities || [])
                      ).map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          <span className="flex items-center gap-2">
                            <MapPinned className="w-3 h-3" />
                            {f.name}
                          </span>
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="text-emerald-600 font-medium">
                        <span className="flex items-center gap-2">
                          <Plus className="w-3 h-3" />
                          Add New Facility
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                  placeholder="e.g. John Smith"
                  data-testid="invite-user-name"
                />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  placeholder="e.g. john.smith@hospital.org"
                  data-testid="invite-user-email"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteUser(false)}>Cancel</Button>
              <Button onClick={handleInviteUser} disabled={submitting} data-testid="invite-user-submit">
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Send className="w-4 h-4 mr-2" />
                Email Setup Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600" />
                Edit User
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={userForm.name}
                  onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                />
              </div>
              {selectedHealthSystem && (
                <div>
                  <Label>Facility / Location</Label>
                  <Select 
                    value={userForm.facility_id || "__none__"} 
                    onValueChange={(value) => setUserForm({...userForm, facility_id: value === "__none__" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No specific facility</SelectItem>
                      {facilities.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          <span className="flex items-center gap-2">
                            <MapPinned className="w-3 h-3" />
                            {f.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={userForm.title}
                    onChange={(e) => setUserForm({...userForm, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Department</Label>
                  <Select 
                    value={userForm.department || ""} 
                    onValueChange={(v) => setUserForm({...userForm, department: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select department..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={userForm.phone}
                    onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Office Location</Label>
                  <Input
                    value={userForm.office_location}
                    onChange={(e) => setUserForm({...userForm, office_location: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={userForm.is_active}
                  onChange={(e) => setUserForm({...userForm, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditUser(false)}>Cancel</Button>
              <Button onClick={handleUpdateUser} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Setup Link Modal */}
        <Dialog open={showSetupLink} onOpenChange={setShowSetupLink}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {setupLinkData?.email_sent ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    Setup Email Sent!
                  </>
                ) : (
                  <>
                    <Link className="w-5 h-5 text-blue-500" />
                    Setup Link Generated
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {setupLinkData?.email_sent && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-emerald-700 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    A setup email has been sent to <strong>{setupLinkData?.user?.email || setupLinkData?.message?.split(' ').pop()}</strong>
                  </p>
                </div>
              )}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 mb-2">User</p>
                <p className="font-semibold text-slate-900">{setupLinkData?.user?.name}</p>
                <p className="text-sm text-slate-600">{setupLinkData?.user?.email}</p>
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500">Password</p>
                  <p className="text-slate-400 italic">User will create during setup</p>
                </div>
              </div>
              <div>
                <Label>Setup Link</Label>
                <div className="mt-1 flex gap-2">
                  <Input value={setupLinkData?.setup_link || ""} readOnly className="font-mono text-xs" />
                  <Button variant="outline" onClick={copySetupLink}><Copy className="w-4 h-4" /></Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Expires: {setupLinkData?.expires_at ? new Date(setupLinkData.expires_at).toLocaleDateString() : '7 days'}</p>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setShowSetupLink(false)}>Close</Button>
              <Button 
                variant="outline"
                onClick={() => {
                  const subject = encodeURIComponent(`Welcome to Ask Isaac  - Your Account Setup`);
                  const body = encodeURIComponent(
`Hi ${setupLinkData?.user?.name},

You've been invited to join Ask Isaac, powered by Sterile Mate — the healthcare facility management platform.

To complete your account setup, please click the link below:

SETUP LINK: ${setupLinkData?.setup_link || ''}

You will create your own secure password during setup.

This link will expire in 7 days.

If you have any questions, please don't hesitate to reach out.

Best regards,
Ask Isaac Team

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
REVOLUTIONIZING HYGIENE IN HEALTHCARE

George Broughton III
President

396 W Greens Road, Suite 1139,
Suite 800A, 800B
Houston, TX 77067

Phone: 800.988.3892
Direct: 281.456.3458
Mobile: 800.988.3892
Email: george.broughton@sterilemate.com`
                  );
                  window.location.href = `mailto:${setupLinkData?.user?.email}?subject=${subject}&body=${body}`;
                }}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Mail className="w-4 h-4 mr-2" />
                Open in Email
              </Button>
              <Button onClick={() => window.open(setupLinkData?.setup_link, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Preview
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Confirm Deletion
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
                {deleteTarget?.type === 'health_system' && (
                  <span className="block mt-2 text-amber-600">
                    ⚠️ All associated users will be deactivated.
                  </span>
                )}
                {deleteTarget?.type === 'facility' && (
                  <span className="block mt-2 text-amber-600">
                    ⚠️ Users assigned to this facility will be unassigned.
                  </span>
                )}
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add Facility Modal */}
        <Dialog open={showAddFacility} onOpenChange={setShowAddFacility}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hospital className="w-5 h-5 text-emerald-600" />
                Add Facility
              </DialogTitle>
            </DialogHeader>
            {renderFacilityForm()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddFacility(false)}>Cancel</Button>
              <Button onClick={handleAddFacility} disabled={submitting} data-testid="submit-add-facility">
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Add Facility
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Facility Modal */}
        <Dialog open={showEditFacility} onOpenChange={setShowEditFacility}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-emerald-600" />
                Edit Facility
              </DialogTitle>
            </DialogHeader>
            {renderFacilityForm()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditFacility(false)}>Cancel</Button>
              <Button onClick={handleUpdateFacility} disabled={submitting} data-testid="submit-edit-facility">
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  function renderFacilityForm() {
    return (
      <div className="space-y-4">
        <div>
          <Label>Facility Name *</Label>
          <Input
            value={facilityForm.name}
            onChange={(e) => setFacilityForm({...facilityForm, name: e.target.value})}
            placeholder="e.g. Ben Taub Hospital"
            data-testid="facility-name-input"
          />
        </div>
        <div>
          <Label>Street Address</Label>
          <Input
            value={facilityForm.address}
            onChange={(e) => setFacilityForm({...facilityForm, address: e.target.value})}
            placeholder="e.g. 1504 Taub Loop"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>City</Label>
            <Input
              value={facilityForm.city}
              onChange={(e) => setFacilityForm({...facilityForm, city: e.target.value})}
              placeholder="Houston"
            />
          </div>
          <div>
            <Label>State</Label>
            <Input
              value={facilityForm.state}
              onChange={(e) => setFacilityForm({...facilityForm, state: e.target.value})}
              placeholder="TX"
              maxLength={2}
            />
          </div>
          <div>
            <Label>ZIP</Label>
            <Input
              value={facilityForm.zip_code}
              onChange={(e) => setFacilityForm({...facilityForm, zip_code: e.target.value})}
              placeholder="77030"
            />
          </div>
        </div>
        <div>
          <Label>Phone</Label>
          <Input
            value={facilityForm.phone}
            onChange={(e) => setFacilityForm({...facilityForm, phone: e.target.value})}
            placeholder="(713) 873-2000"
          />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            value={facilityForm.notes}
            onChange={(e) => setFacilityForm({...facilityForm, notes: e.target.value})}
            placeholder="Additional notes about this facility..."
            rows={2}
          />
        </div>
      </div>
    );
  }

  function renderHealthSystemForm() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Name *</Label>
            <Input
              value={healthSystemForm.name}
              onChange={(e) => setHealthSystemForm({...healthSystemForm, name: e.target.value})}
              placeholder="e.g. Harris Health System"
            />
          </div>
          <div>
            <Label>Short Name</Label>
            <Input
              value={healthSystemForm.short_name}
              onChange={(e) => setHealthSystemForm({...healthSystemForm, short_name: e.target.value})}
              placeholder="e.g. Harris Health"
            />
          </div>
        </div>
        <div>
          <Label>Allowed Email Domains</Label>
          <Input
            value={healthSystemForm.allowed_email_domains}
            onChange={(e) => setHealthSystemForm({...healthSystemForm, allowed_email_domains: e.target.value})}
            placeholder="e.g. harrishealth.org, hhs.org (comma-separated)"
          />
        </div>
        <div className="border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-3">Primary Contact</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contact Name</Label>
              <Input
                value={healthSystemForm.primary_contact_name}
                onChange={(e) => setHealthSystemForm({...healthSystemForm, primary_contact_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={healthSystemForm.primary_contact_email}
                onChange={(e) => setHealthSystemForm({...healthSystemForm, primary_contact_email: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Contact Phone</Label>
              <Input
                value={healthSystemForm.primary_contact_phone}
                onChange={(e) => setHealthSystemForm({...healthSystemForm, primary_contact_phone: e.target.value})}
              />
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-slate-700">Department Contacts</h4>
              <p className="text-xs text-slate-500 mt-0.5">Add multiple contacts per facility (Supply Chain, Facilities, EVS, Bio Med, etc.)</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setHealthSystemForm({
                  ...healthSystemForm,
                  contacts: [
                    ...(healthSystemForm.contacts || []),
                    { name: "", email: "", phone: "", title: "", department: "", is_primary: false }
                  ]
                });
              }}
              data-testid="add-contact-btn"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Add Contact
            </Button>
          </div>

          {/* Bulk-add panel — paste a list of emails for one department */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 mb-3" data-testid="bulk-add-emails-panel">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-900">Bulk Add Emails by Department</span>
              <span className="text-[11px] text-blue-700/70">— paste email-only list</span>
            </div>
            <div className="grid sm:grid-cols-[200px_1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">Department</Label>
                <Select value={bulkEmails.department} onValueChange={(v) => setBulkEmails({ ...bulkEmails, department: v })}>
                  <SelectTrigger data-testid="bulk-emails-department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Supply Chain">Supply Chain</SelectItem>
                    <SelectItem value="Facilities Management">Facilities Management</SelectItem>
                    <SelectItem value="Environmental Services (EVS)">Environmental Services (EVS)</SelectItem>
                    <SelectItem value="Biomedical Engineering (Bio Med)">Biomedical Engineering (Bio Med)</SelectItem>
                    <SelectItem value="Sterile Processing (SPD)">Sterile Processing (SPD)</SelectItem>
                    <SelectItem value="Nursing">Nursing</SelectItem>
                    <SelectItem value="Administration">Administration</SelectItem>
                    <SelectItem value="Finance / Billing">Finance / Billing</SelectItem>
                    <SelectItem value="IT / Informatics">IT / Informatics</SelectItem>
                    <SelectItem value="Infection Prevention">Infection Prevention</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Risk / Compliance">Risk / Compliance</SelectItem>
                    <SelectItem value="Executive Leadership">Executive Leadership</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Email addresses</Label>
                <Textarea
                  rows={2}
                  value={bulkEmails.text}
                  onChange={(e) => setBulkEmails({ ...bulkEmails, text: e.target.value })}
                  placeholder="alice@hosp.org, bob@hosp.org&#10;carol@hosp.org"
                  data-testid="bulk-emails-text"
                  className="text-xs font-mono"
                />
              </div>
              <Button
                type="button"
                variant="default"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  // Parse + dedupe + add as plain contacts to the form
                  const raw = (bulkEmails.text || "")
                    .replace(/;/g, ",")
                    .replace(/\n/g, ",")
                    .split(",")
                    .map(e => e.trim().toLowerCase())
                    .filter(e => /^\S+@\S+\.\S+$/.test(e));
                  if (raw.length === 0) {
                    toast.error("Paste at least one valid email address");
                    return;
                  }
                  const existingEmails = new Set(
                    (healthSystemForm.contacts || []).map(c => (c.email || "").toLowerCase())
                  );
                  const fresh = raw.filter(e => !existingEmails.has(e));
                  if (fresh.length === 0) {
                    toast.info("All addresses are already on the contact list");
                    return;
                  }
                  const newRows = fresh.map(email => ({
                    name: email.split("@")[0],
                    email,
                    phone: "",
                    title: "",
                    department: bulkEmails.department,
                    is_primary: false,
                  }));
                  setHealthSystemForm({
                    ...healthSystemForm,
                    contacts: [...(healthSystemForm.contacts || []), ...newRows]
                  });
                  setBulkEmails({ ...bulkEmails, text: "" });
                  toast.success(`Added ${fresh.length} ${bulkEmails.department} contact${fresh.length === 1 ? "" : "s"}`);
                }}
                data-testid="bulk-emails-add-btn"
              >
                + Add to {bulkEmails.department.split(" ")[0]}
              </Button>
            </div>
          </div>
          {(healthSystemForm.contacts || []).length === 0 ? (
            <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No department contacts yet. Click "Add Contact" to add one.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(healthSystemForm.contacts || []).map((c, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid={`contact-row-${idx}`}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">#{idx + 1}</span>
                      {c.is_primary && (
                        <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Primary</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!c.is_primary}
                          onChange={(e) => {
                            const next = [...(healthSystemForm.contacts || [])];
                            if (e.target.checked) {
                              // Only one primary allowed
                              next.forEach((item, i) => { item.is_primary = i === idx; });
                            } else {
                              next[idx] = { ...next[idx], is_primary: false };
                            }
                            setHealthSystemForm({ ...healthSystemForm, contacts: next });
                          }}
                          data-testid={`contact-${idx}-primary`}
                        />
                        Primary
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          const next = [...(healthSystemForm.contacts || [])];
                          next.splice(idx, 1);
                          setHealthSystemForm({ ...healthSystemForm, contacts: next });
                        }}
                        data-testid={`remove-contact-${idx}-btn`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Name <span className="text-slate-400 font-normal">(optional — defaults to email username)</span></Label>
                      <Input
                        value={c.name || ""}
                        onChange={(e) => {
                          const next = [...(healthSystemForm.contacts || [])];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setHealthSystemForm({ ...healthSystemForm, contacts: next });
                        }}
                        placeholder="Jane Smith"
                        data-testid={`contact-${idx}-name`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input
                        type="email"
                        value={c.email || ""}
                        onChange={(e) => {
                          const next = [...(healthSystemForm.contacts || [])];
                          next[idx] = { ...next[idx], email: e.target.value };
                          setHealthSystemForm({ ...healthSystemForm, contacts: next });
                        }}
                        placeholder="jane@hospital.org"
                        data-testid={`contact-${idx}-email`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Phone</Label>
                      <Input
                        value={c.phone || ""}
                        onChange={(e) => {
                          const next = [...(healthSystemForm.contacts || [])];
                          next[idx] = { ...next[idx], phone: e.target.value };
                          setHealthSystemForm({ ...healthSystemForm, contacts: next });
                        }}
                        placeholder="(281) 555-0100"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={c.title || ""}
                        onChange={(e) => {
                          const next = [...(healthSystemForm.contacts || [])];
                          next[idx] = { ...next[idx], title: e.target.value };
                          setHealthSystemForm({ ...healthSystemForm, contacts: next });
                        }}
                        placeholder="Director / Manager / Buyer"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Department</Label>
                      <Select
                        value={c.department || ""}
                        onValueChange={(value) => {
                          const next = [...(healthSystemForm.contacts || [])];
                          next[idx] = { ...next[idx], department: value };
                          setHealthSystemForm({ ...healthSystemForm, contacts: next });
                        }}
                      >
                        <SelectTrigger data-testid={`contact-${idx}-department`}>
                          <SelectValue placeholder="Select department..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Supply Chain">Supply Chain</SelectItem>
                          <SelectItem value="Facilities Management">Facilities Management</SelectItem>
                          <SelectItem value="Environmental Services (EVS)">Environmental Services (EVS)</SelectItem>
                          <SelectItem value="Biomedical Engineering (Bio Med)">Biomedical Engineering (Bio Med)</SelectItem>
                          <SelectItem value="Sterile Processing (SPD)">Sterile Processing (SPD)</SelectItem>
                          <SelectItem value="Nursing">Nursing</SelectItem>
                          <SelectItem value="Administration">Administration</SelectItem>
                          <SelectItem value="Finance / Billing">Finance / Billing</SelectItem>
                          <SelectItem value="IT / Informatics">IT / Informatics</SelectItem>
                          <SelectItem value="Infection Prevention">Infection Prevention</SelectItem>
                          <SelectItem value="Operations">Operations</SelectItem>
                          <SelectItem value="Risk / Compliance">Risk / Compliance</SelectItem>
                          <SelectItem value="Executive Leadership">Executive Leadership</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-3">Billing Address</h4>
          <div className="space-y-3">
            <Input
              value={healthSystemForm.billing_address}
              onChange={(e) => setHealthSystemForm({...healthSystemForm, billing_address: e.target.value})}
              placeholder="Street Address"
            />
            <div className="grid grid-cols-3 gap-3">
              <Input
                value={healthSystemForm.billing_city}
                onChange={(e) => setHealthSystemForm({...healthSystemForm, billing_city: e.target.value})}
                placeholder="City"
              />
              <Input
                value={healthSystemForm.billing_state}
                onChange={(e) => setHealthSystemForm({...healthSystemForm, billing_state: e.target.value})}
                placeholder="State"
              />
              <Input
                value={healthSystemForm.billing_zip}
                onChange={(e) => setHealthSystemForm({...healthSystemForm, billing_zip: e.target.value})}
                placeholder="ZIP"
              />
            </div>
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            value={healthSystemForm.notes}
            onChange={(e) => setHealthSystemForm({...healthSystemForm, notes: e.target.value})}
            rows={3}
          />
        </div>
      </div>
    );
  }

  // List View (default)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Hospital Management</h2>
          <p className="text-slate-500 text-sm">Manage health systems and hospital users</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await axios.get(`${API}/api/admin/exports/hospitals.csv`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                });
                const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `sterile_mate_hospitals_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (err) { console.error("hospital export failed", err); }
            }}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md ring-1 ring-slate-300 hover:ring-cyan-500 text-sm font-semibold text-slate-700 hover:text-cyan-700 bg-white"
            data-testid="export-hospitals-csv-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <Button
            onClick={() => { resetHealthSystemForm(); setShowAddHealthSystem(true); }}
            variant="outline"
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Add Health System
          </Button>
          <Button
            onClick={() => setShowInviteUser(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search health systems or users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="health-systems">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="health-systems" data-testid="tab-health-systems">
              <Building2 className="w-4 h-4 mr-2" />
              Health Systems ({healthSystems.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-hospital-users">
              <Users className="w-4 h-4 mr-2" />
              Users ({hospitalUsers.length})
            </TabsTrigger>
          </TabsList>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setListViewMode("table")}
              className={`p-2 rounded-md transition-colors ${
                listViewMode === "table" 
                  ? "bg-white shadow-sm text-blue-600" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              title="Table View"
              data-testid="hospital-view-table"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setListViewMode("pipeline")}
              className={`p-2 rounded-md transition-colors ${
                listViewMode === "pipeline" 
                  ? "bg-white shadow-sm text-blue-600" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              title="Pipeline View"
              data-testid="hospital-view-pipeline"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Health Systems Tab */}
        <TabsContent value="health-systems" className="mt-4">
          {listViewMode === "pipeline" ? (
            <HospitalPipeline 
              onHospitalClick={(hospital) => fetchHealthSystemDetail(hospital.id)}
              onRefresh={fetchData}
            />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredHealthSystems.map(hs => (
                <div 
                  key={hs.id} 
                  className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => fetchHealthSystemDetail(hs.id)}
                  data-testid={`health-system-${hs.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{hs.name}</h3>
                        {hs.short_name && <p className="text-sm text-slate-500">{hs.short_name}</p>}
                        {hs.is_network && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 border border-violet-200 rounded px-1.5 py-0.5" title="This hospital is a network parent — other hospitals roll up under it." data-testid={`hs-network-badge-${hs.id}`}>
                            ★ Network
                          </span>
                        )}
                        {hs.parent_name && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5" title={`Subsidiary of ${hs.parent_name}`} data-testid={`hs-parent-badge-${hs.id}`}>
                            ↳ {hs.parent_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>

                  {/* Network drilldown trigger — only on parent networks */}
                  {hs.is_network && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setNetworkDrilldown(hs); }}
                      className="w-full mb-3 px-3 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-bold uppercase tracking-wider hover:from-violet-700 hover:to-indigo-700 transition-colors flex items-center justify-center gap-2"
                      data-testid={`hs-view-network-${hs.id}`}
                    >
                      View network rollup →
                    </button>
                  )}
                  <div className="space-y-2 text-sm mb-4">
                    {hs.primary_contact_email && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {hs.primary_contact_email}
                      </div>
                    )}
                    {hs.billing_city && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {hs.billing_city}, {hs.billing_state}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-500">{hs.facilities?.length || 0} facilities</span>
                    <span className="text-xs text-slate-500">
                      {hospitalUsers.filter(u => u.health_system_id === hs.id).length} users
                    </span>
                  </div>

                  {/* Sales-rep assignment (admin-only — already gated by /admin/* route) */}
                  <div
                    className="mt-3 pt-3 border-t border-dashed border-slate-200"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`hs-rep-row-${hs.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-500 uppercase tracking-wide font-bold">Sales Rep</span>
                      {hs.assigned_rep_name ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                          <CheckCircle className="w-3 h-3" /> {hs.assigned_rep_name}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-600 font-bold">
                          <Clock className="w-3 h-3" /> Unassigned
                        </span>
                      )}
                    </div>
                    <select
                      value={hs.assigned_rep_id || ""}
                      onChange={(e) => assignRepToHospital(hs.id, e.target.value, e)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                      data-testid={`hs-rep-select-${hs.id}`}
                    >
                      <option value="">— Unassigned —</option>
                      {reps.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Network hierarchy controls */}
                  <div
                    className="mt-2 pt-2 border-t border-dashed border-slate-200"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`hs-network-row-${hs.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 text-xs mb-1">
                      <span className="text-slate-500 uppercase tracking-wide font-bold">Network</span>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!hs.is_network}
                          onChange={(e) => toggleNetworkRole(hs.id, e.target.checked, e)}
                          className="accent-violet-600"
                          data-testid={`hs-is-network-${hs.id}`}
                        />
                        <span className="text-slate-600">This IS a parent network</span>
                      </label>
                    </div>
                    {!hs.is_network && (
                      <select
                        value={hs.parent_id || ""}
                        onChange={(e) => setParentNetwork(hs.id, e.target.value || null, e)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                        data-testid={`hs-parent-select-${hs.id}`}
                      >
                        <option value="">— Standalone (no parent) —</option>
                        {(healthSystems || []).filter((x) => x.is_network && x.id !== hs.id).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
              {filteredHealthSystems.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No health systems found</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">User</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Health System</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Department</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Password</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50" data-testid={`hospital-user-${user.id}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => fetchUserDetail(user.id)} className="text-left hover:text-blue-600">
                        <p className="font-medium text-slate-900">{user.name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{user.health_system_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{user.department || '-'}</td>
                    <td className="px-4 py-3">
                      {user.profile_complete ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : user.setup_token ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-400 italic">••••••••</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!user.profile_complete && (
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleResendSetupLink(user.id); }}>
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedUser(user); openEditUser(user); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ type: 'user', id: user.id, name: user.name });
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No users found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {renderModals()}
      
      {/* Network drilldown drawer — opened from network parent cards */}
      <NetworkDrilldownDrawer
        network={networkDrilldown}
        open={!!networkDrilldown}
        onOpenChange={(o) => { if (!o) setNetworkDrilldown(null); }}
        token={token}
        onOpenHospital={(hsId) => {
          setNetworkDrilldown(null);
          fetchHealthSystemDetail(hsId);
        }}
      />

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={showBulkEmailModal}
        onClose={() => {
          setShowBulkEmailModal(false);
          setSelectedHospitalForEmail(null);
        }}
        token={token}
        preselectedType="specific_hospital"
        preselectedHospitalId={selectedHospitalForEmail?.id}
        preselectedHospitalName={selectedHospitalForEmail?.name}
      />
    </div>
  );
}
